import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { UserPublic } from '@mondy/shared';
import { api } from '@/api';
import { clearToken, getToken } from '@/lib/auth';

const QUERY_KEY = ['auth', 'me'] as const;

/**
 * Returns `undefined` while the first fetch is in flight, then `UserPublic | null`.
 * Unauthenticated, disabled, and network-failure states all resolve to `null`.
 *
 * The result is shared across the app via the QueryClient cache — pages mounting
 * within `staleTime` don't refetch, so there's no auth flash when navigating.
 */
export function useCurrentUser(): UserPublic | null | undefined {
  const q = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      // If we don't even have a token in storage, short-circuit to null without
      // touching the network.
      if (!getToken()) return null;
      try {
        const res = await api.get<{ user: UserPublic | null }>('/api/auth/me');
        return res.user;
      } catch {
        return null;
      }
    },
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    retry: false,
  });

  if (q.isLoading && q.data === undefined) return undefined;
  return q.data ?? null;
}

/**
 * Invalidate the cached `/auth/me` so the next render pulls fresh state.
 * Call this after login / signup / logout.
 */
export function useRefreshCurrentUser() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: QUERY_KEY });
}

/**
 * Convenience logout: clear the token and refresh the `me` query.
 */
export function useLogout() {
  const qc = useQueryClient();
  return () => {
    clearToken();
    qc.setQueryData(QUERY_KEY, null);
  };
}
